// Story 8.6 (ADO-2015): "Submit now" in an open cart on the choice screen (Figma 5031:4608) sends
// that cart at once, with no review step.
//
//   selectOrderingChoice(entry.key, accountCode);           // the take-up, first
//   const result = await submitOpenCart(cartTargetOf(entry, accountCode), {
//     seenRevision: listed.revision,                        // the open-carts entry's revision
//   });
//   // 'open-failed' | 'superseded' | 'settled'
//
// The caller takes the cart up before calling: the take-up is a change of cart, and a change of
// cart `clear()`s the submission store, which would drop the answer of a send already started. So
// this refuses (`'open-failed'`, nothing opened or sent) unless the session is already on that
// cart: its depot, Story 8.4 (ADO-2013) its account (two accounts at one Value Centre are two
// carts), and Enabler E8-G (ADO-3109, DA-8) its entry kind (Chill and Fruit & Veg at depot 25
// are two carts).
//
// Then it opens that cart (`useActiveBasketStore.open`, get-or-open) and sends it through
// the one send path, `useOrderSubmissionStore.submit`, so the key, the timeout, the
// `cart-changed` re-open and the on-order refresh are the Order Review page's. Enabler E8-G
// (ADO-3109), DA-1: "Submit now" has no cart view of its own, so the send carries the revision
// the open-carts list showed for the cart (`seenRevision`): a cart changed since it was listed is
// refused (card 09) and settles as a failure, rather than being sent as it is now, whether or not
// the basket read of the take-up already holds the newer cart (unless the basket holds the listed
// revision, the refusal marks no line and is not sent again). A line edit still
// settling, a previous order being added or a clear in progress is waited for, since `submit`
// refuses a cart in that state. The results:
//   - `'open-failed'`: the cart could not be opened, and nothing was sent;
//   - `'superseded'`: the session left the cart before the send started (nothing sent), or this
//     send has no outcome of its own in the submission store (the session left mid-send and the
//     switch dropped the answer, or another attempt was already in flight);
//   - `'settled'`: this send ended, and the submission store holds its outcome for the depot
//     (`'sent'` or `'failed'`, a confirmation or error this call produced, not one held from
//     an earlier send). The choice screen confirms a `'sent'` in its "Order sent" dialog and
//     opens the Order Review page for a `'failed'`, which shows why.
//
// The reference: one already held for the cart (typed in the Order Review footer) is sent as
// it is. Otherwise, when the depot's account requires one (SL1.0 `cust_ord`, refused with "Your
// Order Reference is mandatory.") the Trading Account code is sent, and also when the rule
// cannot be read, since a present reference is never refused for being present. It is written
// through `setCustomerOrderReference` just before the send, so the footer's field shows it if the
// send fails. A cart with no account gets no reference filled in.
//
// Story 8.13 (ADO-2022), D-7: the fulfilment choice. No Fulfilment card is on this screen, so the
// send carries only what the Shopper touched on the Order Review page (nothing touched, no
// fulfilment fields: "Submit now" sends without asking for fulfilment options). A refused choice
// settles as a failure, which the choice screen answers by opening the Order Review page, where
// the refusal is shown on its fields.
//
// One sequence per cart at a time: a second call while one is in flight answers with the first.

import { cartTargetKey, sameCartTarget, type CartTarget } from '../api/cart-target';
import { sessionCartKey } from './sessionCart';
import { useActiveBasketStore } from './useActiveBasketStore';
import { useBasketStore } from './useBasketStore';
import { loadOrderReferenceRule } from './useOrderReferenceRule';
import { referenceKeyAt, useOrderSubmissionStore } from './useOrderSubmissionStore';

export type SubmitOpenCartResult = 'open-failed' | 'superseded' | 'settled';

const inFlight = new Map<string, Promise<SubmitOpenCartResult>>();

function heldReference(depotId: string): string {
  const references = useOrderSubmissionStore.getState().customerOrderReferenceByCart;
  return references[referenceKeyAt(depotId)]?.trim() ?? '';
}

/** Whether the send should carry the account code, decided before anything is held. */
async function needsAccountCode(cart: CartTarget): Promise<boolean> {
  if (cart.customerAccountCode === null || heldReference(cart.depotId) !== '') return false;
  const rule = await loadOrderReferenceRule(cart);
  return rule === null || rule.required;
}

function basketOpenFor(cart: CartTarget): boolean {
  const active = useActiveBasketStore.getState();
  return active.status === 'ready' && sameCartTarget(active.cart, cart) && active.basket !== null;
}

/** Resolves once no basket write is settling (the state `submit` refuses to send from). */
function basketSettled(): Promise<void> {
  const settled = () => {
    const basket = useBasketStore.getState();
    return basket.pending.size === 0 && !basket.addingFromOrder && !basket.clearing;
  };
  if (settled()) return Promise.resolve();
  return new Promise((resolve) => {
    const unsubscribe = useBasketStore.subscribe(() => {
      if (!settled()) return;
      unsubscribe();
      resolve();
    });
  });
}

async function run(cart: CartTarget, listed: ListedOpenCart): Promise<SubmitOpenCartResult> {
  const { depotId, customerAccountCode } = cart;
  const onCart = () => sessionCartKey() === cartTargetKey(cart);
  if (!onCart()) return 'open-failed';
  const [withAccountCode] = await Promise.all([
    needsAccountCode(cart),
    useActiveBasketStore.getState().open(cart),
  ]);
  if (!onCart()) return 'superseded';
  if (!basketOpenFor(cart)) return 'open-failed';
  await basketSettled();
  if (!onCart()) return 'superseded';
  if (!basketOpenFor(cart)) return 'open-failed';
  if (withAccountCode && customerAccountCode !== null && heldReference(depotId) === '') {
    useOrderSubmissionStore.getState().setCustomerOrderReference(depotId, customerAccountCode);
  }
  const before = useOrderSubmissionStore.getState();
  await before.submit(
    depotId,
    listed.seenRevision === undefined ? {} : { seenRevision: listed.seenRevision },
  );
  const after = useOrderSubmissionStore.getState();
  if (after.depotId !== depotId) return 'superseded';
  if (after.phase === 'sent' && after.confirmation !== before.confirmation) return 'settled';
  if (after.phase === 'failed' && after.error !== before.error) return 'settled';
  return 'superseded';
}

/** What the open-carts list showed for the cart (Enabler E8-G, ADO-3109, DA-1). */
export interface ListedOpenCart {
  /** The revision the list showed; absent when it showed none, and the send then carries the
   *  revision of the basket on screen. */
  readonly seenRevision?: number;
}

/** Open `cart` and send it at once (see the file header). The caller has already taken the
 *  cart up. */
export function submitOpenCart(
  cart: CartTarget,
  listed: ListedOpenCart = {},
): Promise<SubmitOpenCartResult> {
  const key = cartTargetKey(cart);
  const pending = inFlight.get(key);
  if (pending !== undefined) return pending;
  const sequence = run(cart, listed).finally(() => inFlight.delete(key));
  inFlight.set(key, sequence);
  return sequence;
}
